import { present, upperCamelCase } from '@art-suite/art-core-ts';
import {
  getEffectiveScanValue,
  getNumberScanValueOrUndefined,
  getScanTemplateValueKeysInDependencyOrder,
  getScanValueHasOverride,
  isExpressionValue,
  isNumberScanValue,
  ScanGroupOptimized,
  ScanTaskOptimized,
  ScanTemplateValue,
  ScanTemplateValues,
  ScanValue,
  ScanValues,
  ScanValueType,
} from '@kaiizen/kai-scan-template-system';
import { presentScanValueEquation, presentScanValueLabel } from './lineItemPresenters';
import { truncateWithEllipsis } from './textPresenters';

/** presented part names and custom labels are each never longer than this; new template part names are capped to it in the editor */
export const MAX_TASK_NAME_LENGTH = 200;

const nameWithCustomLabelPresenter = (name: string, customLabel: string | undefined, maxLength: number) =>
  customLabel?.trim()
    ? `${truncateWithEllipsis(name, maxLength)} (${truncateWithEllipsis(customLabel.trim(), maxLength)})`
    : truncateWithEllipsis(name, maxLength);

export const groupNamePresenter = (group: ScanGroupOptimized) => nameWithCustomLabelPresenter(group.template.name, group.customLabel, MAX_TASK_NAME_LENGTH);

/**
 * The group's short name for tight layouts such as plan labels: its template
 * name, followed by its custom label when that is only a number ("Bedroom 2"),
 * or the custom label alone when it says more ("Primary Bedroom").
 */
export const groupShortNamePresenter = (group: ScanGroupOptimized) => {
  const label = group.customLabel?.trim();
  const name = !label ? group.template.name : /^\d+$/.test(label) ? `${group.template.name} ${label}` : label;
  return truncateWithEllipsis(name, MAX_TASK_NAME_LENGTH);
};

/**
 * The group's name in each of the floor plan's naming styles: the full name
 * ("Bedroom (Office)"), the group alone ("Bedroom"), and the label alone
 * ("Office"), which is the group's name when it has no label.
 */
export const groupNameStylesPresenter = (group: ScanGroupOptimized): { full: string; group: string; label: string } => ({
  full: groupNamePresenter(group),
  group: truncateWithEllipsis(group.template.name, MAX_TASK_NAME_LENGTH),
  label: truncateWithEllipsis(group.customLabel?.trim() || group.template.name, MAX_TASK_NAME_LENGTH),
});

export const taskNamePresenter = (task: ScanTaskOptimized) => nameWithCustomLabelPresenter(task.template.name, task.customLabel, MAX_TASK_NAME_LENGTH);

/**
 * Human label for a group template's `subtype` (e.g. "otherBedroom" -> "Bedroom").
 * `subtype` is only set for room-like groups (bedrooms/bathrooms) today - most
 * groups (kitchens, living rooms, exterior features) have no subtype at all,
 * so callers should treat `undefined` as "no type to show", not fall back to
 * a generic placeholder here.
 */
export const groupSubtypePresenter = (subtype: string): string => {
  if (subtype === 'otherBedroom') return 'Bedroom';
  if (subtype === 'otherBathroom') return 'Bathroom';
  if (subtype === 'mainBedroom') return 'Primary Bedroom';
  if (subtype === 'mainBathroom') return 'Primary Bathroom';
  return upperCamelCase(subtype, ' ');
};

export const photoTagsPresenter = (tag: string) => {
  switch (tag) { // for the ScanType tags
    case 'initial':
      return 'Initial Walk';
    case 'changeRequest':
      return 'Change Order';
    case 'progressRework':
      return 'Punch List';
    case 'progress':
      return 'Progress Walk';
    case 'progressCompleted':
      return 'Close Out';
    case 'final':
      return 'Close Out';
    default:
      return tag;
  }
};

/** Measurements round to the precision their size warrants: hundredths below 10, tenths below 100, whole numbers from 100 up. */
const getMeasurementMaximumFractionDigits = (value: number) => Math.abs(value) < 10 ? 2 : Math.abs(value) < 100 ? 1 : 0;

/**
 * Formats a scan value for display according to its template value's type. Numeric types render with
 * locale grouping and the unit appended; `usd` renders as `$` with two decimals, and `count*` types or
 * `showUnit === false` omit the unit. Other numbers round by size (see `getMeasurementMaximumFractionDigits`).
 * Zero is a real value and renders (`$0.00`, `0 sqft`); only a missing or unparsable number renders as ''.
 * Text and checkbox values render as their string form, or '' when missing.
 */
export const scanValuePresenter = (value: ScanValueType, templateValue: ScanTemplateValue, showUnit: boolean = true): string => {
  if (isNumberScanValue(templateValue)) {
    const numberValue = getNumberScanValueOrUndefined(value);
    if (numberValue == null) return '';
    if (templateValue.type === 'usd') return `$${numberValue.toFixed(2)}`;
    showUnit = showUnit && !!templateValue.type && !/^count/.test(templateValue.type);
    const presented = numberValue.toLocaleString(undefined, { maximumFractionDigits: getMeasurementMaximumFractionDigits(numberValue) });
    return presented + (showUnit ? ` ${templateValue.type}` : '');
  }
  return value == null ? '' : String(value);
};

/** `overage` is stored as a fraction (0.1) but reads as a percent ("10%"). No template type says "percent" yet, so the key is the tell. */
const OVERAGE_VALUE_KEY = 'overage';
/** `coverage` is typed as a count but covers measurement units (15 sqft per box), so it reads in the measurement's unit. */
const COVERAGE_VALUE_KEY = 'coverage';
const MEASUREMENT_VALUE_KEY = 'measurement';

/** `scanValuePresenter`, plus the presentation a value's key implies among its part's values: `overage` reads as a percent, `coverage` in the measurement's unit. */
export const scanValueByKeyPresenter = (key: string, value: ScanValueType | undefined, valueTemplates: ScanTemplateValues): string => {
  if (key === OVERAGE_VALUE_KEY) {
    const overage = getNumberScanValueOrUndefined(value);
    if (overage != null) return `${(overage * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  }
  const measurementType = valueTemplates[MEASUREMENT_VALUE_KEY]?.type;
  if (key === COVERAGE_VALUE_KEY && measurementType) return scanValuePresenter(value ?? null, { ...valueTemplates[key], type: measurementType });
  return scanValuePresenter(value ?? null, valueTemplates[key]);
};

/** One row of a scan part's values tooltip: the value's key and label, its presented effective value, and the equation it's computed from, if any. */
export type ScanValuesTooltipRow = { key: string; label: string; presentedValue: string; equation?: string };

/**
 * The rows of a tooltip listing a scan part's values, each labeled as the UX names it (presentScanValueLabel) and
 * presented by its template's type, in dependency order (see `getScanTemplateValueKeysInDependencyOrder`). A value
 * computed from an equation carries the equation in the same names (presentScanValueEquation), unless the user has
 * overridden it: then the equation no longer applies.
 */
export const getScanValuesTooltipRows = (values: ScanValues | undefined, valueTemplates: ScanTemplateValues | undefined): ScanValuesTooltipRow[] =>
  getScanTemplateValueKeysInDependencyOrder(valueTemplates).map(key => {
    const templateValue = valueTemplates![key];
    const value = values?.[key];
    const presentedValue = scanValueByKeyPresenter(key, getEffectiveScanValue(value), valueTemplates!);
    const label = presentScanValueLabel(key, valueTemplates);
    return isExpressionValue(templateValue) && !getScanValueHasOverride(value)
      ? { key, label, presentedValue, equation: presentScanValueEquation(templateValue.value, valueTemplates) }
      : { key, label, presentedValue };
  });

/**
 * The string an `<input>` should hold for a scan value. A `number` input presents numbers itself, so it takes the
 * bare number (no unit, digit grouping, or currency symbol) and '' when there is none; any other input type takes
 * the `scanValuePresenter` string.
 */
export const getScanValueInputString = (
  value: ScanValueType | undefined,
  templateValue: ScanTemplateValue,
  inputType: 'number' | 'text',
): string =>
  inputType === 'number'
    ? String(getNumberScanValueOrUndefined(value) ?? '')
    : scanValuePresenter(value ?? null, templateValue);

/**
 * Tooltip text for an overridden scan value: the template's calculated value and the user's override,
 * each formatted with `scanValuePresenter`. Empty unless both values are present; pair with
 * `getScanValueHasOverride` to decide whether to show it.
 */
export const getScanValueOverrideTooltip = (valueObj: ScanValue, templateValue: ScanTemplateValue): string => {
  if (!valueObj) return '';
  const { setValue, calculatedValue } = valueObj;
  if (!present(setValue) || !present(calculatedValue)) return '';

  const templateDisplay = scanValuePresenter(calculatedValue, templateValue);
  const overrideDisplay = scanValuePresenter(setValue, templateValue);
  return `Default value: ${templateDisplay}\nOverride value: ${overrideDisplay}`;
};
